import {useEffect, useMemo, useState, Fragment} from 'react';
import {Layout} from '@/components/Layout';
import {Download, Grid3x3, List, Search, QrCode, Package, Ban, Car, Info, Layers, Send, FileText, ChevronDown, ChevronRight} from 'lucide-react';
import {useExternalContacts} from '@/contexts/ExternalContactsContext';
import {useNavigate} from 'react-router-dom';
import {Input} from '@/components/ui/input';
import {Checkbox} from '@/components/ui/checkbox';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Button} from '@/components/ui/button';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter} from '@/components/ui/dialog';
import {Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle} from '@/components/ui/sheet';
import {Label} from '@/components/ui/label';
import {Textarea} from '@/components/ui/textarea';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {toast} from 'sonner';
import {Package as BoxIcon, Calendar} from 'lucide-react';
import {api, fetchTagBatches, getApiErrorMessage, objectTypeEndpoints, resourceEndpoints, tagDeactivationReasonEndpoints, tagEndpoints, tagRequestEndpoints} from '@/lib/api';
import {usePermissions} from '@/hooks/usePermissions';
import {PERMISSIONS} from '@/lib/permissions';

const TAG_ACTION_PERMISSIONS = {
    create: PERMISSIONS.TAG_CREATE,
    read: PERMISSIONS.TAG_READ,
    update: PERMISSIONS.TAG_UPDATE,
    delete: PERMISSIONS.TAG_DELETE,
};

const TAG_REQUEST_ACTION_PERMISSIONS = {
    create: PERMISSIONS.TAG_REQUEST_CREATE,
    read: PERMISSIONS.TAG_REQUEST_READ,
    update: PERMISSIONS.TAG_REQUEST_UPDATE,
    delete: PERMISSIONS.TAG_REQUEST_DELETE,
};

const hideChargersInfo = true;
const chargerInfoPattern = /(^|[\s/_-])(chargers?|charging|evse|carregadores?|carregador|posto\s+carregamento)([\s/_-]|$)/i;
const isChargerInfo = (...values) => (
    values.some(value => chargerInfoPattern.test(String(value ?? '')))
);

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const unwrapAttributes = (source = {}) => {
    const value = source?.data && typeof source.data === 'object' && !Array.isArray(source.data)
        ? source.data
        : source;
    return value?.attributes && typeof value.attributes === 'object' && !Array.isArray(value.attributes)
        ? {...value, ...value.attributes}
        : value;
};

const objectTypeKey = (objectType) =>
    String(objectType?.id || objectType?.object_type_id || objectType?.company_object_type_id || objectType?.type_id || objectType?.slug || '').trim();

const batchKey = (batch) =>
    String(batch?.id ?? batch?.batch_id ?? batch?.tag_batch_id ?? batch?.gest_tag_batch_id ?? batch?.gest_tag_batches_id ?? '').trim();

const objectTypeIdFrom = (...sources) => {
    for (const source of sources) {
        const item = unwrapAttributes(source || {});
        const id = item?.object_type_id ?? item?.company_object_type_id ?? item?.gest_object_type_id ?? item?.type_id ?? item?.id;
        if (id !== undefined && id !== null && id !== '') return id;
    }
    return null;
};

const objectTypeLabelFrom = (objectType) => {
    const item = unwrapAttributes(objectType || {});
    return item?.name || item?.label || item?.title || '';
};

const textValueFrom = (...values) => {
    for (const value of values) {
        if (value === undefined || value === null || value === '') continue;
        if (typeof value === 'string' || typeof value === 'number') {
            const text = String(value).trim();
            if (text) return text;
            continue;
        }

        const item = unwrapAttributes(value);
        const text = item?.name ?? item?.label ?? item?.title ?? item?.description ?? item?.type ?? item?.slug;
        if (text !== undefined && text !== null && String(text).trim()) return String(text).trim();
    }
    return '';
};

const packSizeFrom = (source) => {
    const value = source?.pack_size ?? source?.tag_pack_size ?? source?.tags_per_pack ?? source?.req_pack_size;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const normalizeObjectTypeTemplates = (templates = []) =>
    (Array.isArray(templates) ? templates : []).map((source, index) => {
        const template = unwrapAttributes(source);
        const id = template?.id ?? template?.object_type_template_id ?? template?.company_object_type_template_id ?? template?.uuid ?? index + 1;
        return {
            ...template,
            id,
            name: template?.name || template?.label || template?.title || `Modelo ${index + 1}`,
            pack_size: packSizeFrom(template),
        };
    }).filter(template => template.id);

const normalizeObjectTypes = (payload) =>
    (collectionPayload(payload, 'object_types').length ? collectionPayload(payload, 'object_types') : collectionPayload(payload, 'types')).map(source => {
        const objectType = unwrapAttributes(source);
        const id = objectTypeKey(objectType);
        return {
            ...objectType,
            id,
            name: objectType?.name || objectType?.label || objectType?.title || `Tipo ${id}`,
            category: objectType?.category || objectType?.group || objectType?.name || objectType?.label || objectType?.title || '',
            pack_size: packSizeFrom(objectType),
            templates: normalizeObjectTypeTemplates(objectType?.templates),
        };
    }).filter(objectType => objectType.id);

const isChargerObjectType = (objectType = {}) => (
    isChargerInfo(objectType.name, objectType.label, objectType.title, objectType.category, objectType.type, objectType.group, objectType.slug)
);

const normalizeTagStatus = (tag, resource) => {
    if (tag.status_key || tag.status_title || tag.status_badge_class) {
        const key = String(tag.status_key || '').trim();
        return {
            key,
            label: tag.status_title || key,
            className: tag.status_badge_class || 'bg-slate-100 text-slate-700',
        };
    }

    if (tag.deactivated_at || tag.gest_tag_deactivation_reason_id || tag.deactivate_reason_id || tag.deactivation_reason_id || tag.deativation_reason_id) {
        return {key: 'inativa', label: 'inativa', className: 'bg-slate-100 text-slate-700'};
    }
    if (tag.gest_resource_id || tag.resource_id || resource?.id) {
        return {key: 'atribuida', label: 'atribuida', className: 'bg-slate-100 text-slate-700'};
    }
    const value = String(tag.status ?? '').toLowerCase();
    const map = {
        0: 'armazem',
        1: 'atribuida',
        2: 'inativa',
        available: 'armazem',
        warehouse: 'armazem',
        armazem: 'armazem',
        assigned: 'atribuida',
        atribuida: 'atribuida',
        active: 'ativa',
        ativa: 'ativa',
        inactive: 'inativa',
        inativa: 'inativa',
    };
    const key = map[value] || 'armazem';
    return {key, label: key, className: 'bg-slate-100 text-slate-700'};
};

const normalizeTag = (source = {}) => {
    const tag = unwrapAttributes(source);
    const batch = unwrapAttributes(tag.gest_tag_batch ?? tag.tag_batch ?? tag.batch ?? {});
    const resource = unwrapAttributes(tag.gest_resource ?? tag.resource ?? {});
    const objectType = unwrapAttributes(batch.object_type ?? tag.object_type ?? {});
    const resourceObjectType = unwrapAttributes(resource.object_type ?? resource.company_object_type ?? {});
    const manager = unwrapAttributes(resource.company_manager ?? resource.manager ?? resource.driver ?? {});
    const department = unwrapAttributes(resource.company_department ?? {});
    const departmentName = textValueFrom(department.name) || '-';
    const reference = tag.reference ?? tag.serial_no ?? tag.code ?? source.id ?? tag.id;
    const vehicle = resource.reference ?? resource.name ?? resource.title ?? resource.code ?? '-';
    const resourceName = textValueFrom(resource.name) || '-';
    const resourceType = textValueFrom(resource.type) || '-';
    const resourceReference = resource.reference ?? '-';
    const objectTypeId = objectTypeIdFrom(
        {
            object_type_id: batch.object_type_id,
            company_object_type_id: batch.company_object_type_id,
            gest_object_type_id: batch.gest_object_type_id,
            type_id: batch.type_id,
        },
        objectType,
        resourceObjectType,
        {
            object_type_id: resource.object_type_id,
            company_object_type_id: resource.company_object_type_id,
            gest_object_type_id: resource.gest_object_type_id,
            type_id: resource.type_id,
        },
        {
            object_type_id: tag.object_type_id,
            company_object_type_id: tag.company_object_type_id,
            gest_object_type_id: tag.gest_object_type_id,
            type_id: tag.type_id,
        }
    );
    const batchId = (tag.gest_tag_batch_id ?? tag.gest_tag_batches_id ?? tag.tag_batch_id ?? tag.batch_id ?? batchKey(batch)) || null;
    const objectTypeName = objectTypeLabelFrom(objectType)
        || objectTypeLabelFrom(resourceObjectType)
        || batch.objectTypeName
        || batch.object_type_name
        || (objectTypeId ? `Tipo ${objectTypeId}` : 'Sem tipo de objeto');
    const status = normalizeTagStatus(tag, resource);
    return {
        id: String(reference || ''),
        uuid: tag.uuid,
        tagId: source.id ?? tag.id,
        resourceId: resource.id ?? resource.resource_id ?? tag.resource_id ?? tag.gest_resource_id ?? null,
        serialNo: tag.serial_no ?? '',
        objectTypeId,
        batchId,
        objectType: objectTypeName,
        category: departmentName,
        status: status.key,
        statusLabel: status.label,
        statusClass: status.className,
        vehicle,
        resourceName,
        resourceType,
        resourceReference,
        user: manager.name ?? manager.full_name ?? '-',
        location: tag.placement ?? resource.location ?? resource.location_name ?? '-',
        lastIncident: tag.last_incident_at ?? tag.lastIncident ?? '-',
        createdAt: String(tag.created_at ?? '').slice(0, 10),
        updatedAt: String(tag.updated_at ?? '').slice(0, 10),
    };
};

const normalizeTags = (payload) =>
    collectionPayload(payload, 'tags')
        .map(normalizeTag)
        .filter(tag => tag.id)
        .filter(tag => !hideChargersInfo || !isChargerInfo(tag.objectType, tag.category, tag.vehicle, tag.resourceName, tag.resourceType, tag.resourceReference, tag.location));

const normalizeDeactivationReason = (source = {}, fallback = {}) => ({
    ...fallback,
    ...source,
    id: source.id ?? source.tag_deactivation_reason_id ?? source.deactivation_reason_id ?? fallback.id,
    name: source.name ?? source.label ?? fallback.name ?? '',
    description: source.description ?? fallback.description ?? '',
    status: Number(source.status ?? fallback.status ?? 0),
});

const normalizeDeactivationReasons = (payload) => {
    const reasons = collectionPayload(payload, 'tag_deactivation_reasons');
    const deactivationReasons = reasons.length ? reasons : collectionPayload(payload, 'deactivation_reasons');
    const namedReasons = deactivationReasons.length ? deactivationReasons : collectionPayload(payload, 'reasons');
    const items = namedReasons.length ? namedReasons : collectionPayload(payload, 'tagDeactivationReasons');
    return items.map((item, index) => normalizeDeactivationReason(item, {id: index + 1})).filter(reason => reason.id && reason.name);
};

const resourceTypeFrom = (resource, objectType) => {
    const value = String(resource.type || resource.resource_type || objectType.slug || objectType.name || objectType.title || '').toLowerCase();
    if (value.includes('carreg') || value.includes('charger')) return 'carregador';
    if (value.includes('veic') || value.includes('vehic') || value.includes('carro') || value.includes('car')) return 'veiculo';
    return 'outro';
};

const normalizeAssignableResource = (source = {}) => {
    const resource = unwrapAttributes(source);
    const objectType = unwrapAttributes(resource.object_type ?? {});
    const manager = unwrapAttributes(resource.company_manager ?? resource.manager ?? resource.driver ?? {});
    const department = unwrapAttributes(resource.company_department ?? {});
    const id = source.id ?? resource.id;
    const identifier = resource.reference ?? String(id || '');
    const label = resource.name ?? resource.title ?? resource.description ?? objectType.title ?? objectType.name ?? `Recurso ${identifier}`;
    const resourceName = textValueFrom(resource.name) || '-';
    const resourceType = textValueFrom(resource.type) || '-';

    return {
        uid: String(id ?? resource.uuid ?? identifier),
        id,
        uuid: resource.uuid ?? source.uuid ?? null,
        identifier: String(identifier || label),
        label,
        resourceName,
        type: resourceTypeFrom(resource, objectType),
        resourceType,
        category: textValueFrom(department.name) || '-',
        objectType: objectTypeLabelFrom(objectType),
        objectIcon: objectType.icon ?? null,
        hasTag: Boolean(resource.gest_tag_id || resource.tag_id || resource.tag || resource.gest_tag),
        managerName: manager.name ?? manager.full_name ?? '',
    };
};

const normalizeAssignableResources = (payload) =>
    collectionPayload(payload, 'resources')
        .map(normalizeAssignableResource)
        .filter(resource => resource.uid && resource.identifier)
        .filter(resource => !hideChargersInfo || (
            resource.type !== 'carregador'
            && !isChargerInfo(resource.identifier, resource.label, resource.resourceType, resource.category, resource.objectType)
        ));

const multiplierOptionsFrom = (source) => {
    const options = source?.multipliers || source?.multiplier_options || source?.allowed_multipliers;
    if (Array.isArray(options)) {
        return options.map(Number).filter(value => Number.isFinite(value) && value > 0);
    }

    const maxMultiplier = Number(source?.max_multiplier);
    if (Number.isInteger(maxMultiplier) && maxMultiplier > 0) {
        return Array.from({length: maxMultiplier}, (_, index) => index + 1);
    }

    const multiplier = Number(source?.multiplier ?? source?.req_multiplier ?? source?.default_multiplier ?? source?.minimum_multiplier);
    return Number.isFinite(multiplier) && multiplier > 0 ? [multiplier] : [];
};

const toApiId = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    return Number.isInteger(number) ? number : value;
};

const assignTagPayload = (tag = {}, resource = {}) => ({
    id: toApiId(tag.tagId ?? tag.id),
    tag_id: toApiId(tag.tagId),
    tag_uuid: tag.uuid ?? null,
    tag_reference: tag.id ?? null,
    resource_id: toApiId(resource.id),
    resource_uuid: resource.uuid ?? null,
    gest_resource_id: toApiId(resource.id),
});

const removeTagAssignmentPayload = (tag = {}, markDeactivated = false, reasonId = '', notes = '') => ({
    id: toApiId(tag.tagId ?? tag.id),
    tag_id: toApiId(tag.tagId),
    tag_uuid: tag.uuid ?? null,
    tag_reference: tag.id ?? null,
    resource_id: toApiId(tag.resourceId),
    gest_resource_id: toApiId(tag.resourceId),
    deactivate: Boolean(markDeactivated),
    deactivated: Boolean(markDeactivated),
    mark_as_deactivated: Boolean(markDeactivated),
    deactivation_reason_id: markDeactivated ? toApiId(reasonId) : null,
    deativation_reason_id: markDeactivated ? toApiId(reasonId) : null,
    gest_tag_deactivation_reason_id: markDeactivated ? toApiId(reasonId) : null,
    notes: markDeactivated ? notes : '',
    deactivation_notes: markDeactivated ? notes : '',
});

// Module-level so both TagsPage and TagRow (declared outside TagsPage) can access it.
const StatusBadgeCustom = ({tag}) => {
    const label = tag?.statusLabel || tag?.status;
    if (!label) return null;
    return <Badge className={`${tag?.statusClass || 'bg-slate-100 text-slate-700'} border-0`}>{label}</Badge>;
};


export const TagsPage = () => {
    const navigate = useNavigate();
    const {contactTypes} = useExternalContacts();
    const {can} = usePermissions();
    const [tags, setTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(true);
    const [tagsError, setTagsError] = useState('');
    const [resources, setResources] = useState([]);
    const [resourcesLoading, setResourcesLoading] = useState(true);
    const [resourcesError, setResourcesError] = useState('');
    const [viewMode, setViewMode] = useState('list');
    const [groupBy, setGroupBy] = useState('objectType-batch'); // 'none' | 'objectType' | 'objectType-batch'
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [isAssignOpen, setIsAssignOpen] = useState(false);
    const [selectedTag, setSelectedTag] = useState(null);
    const [assignTargetReference, setAssignTargetReference] = useState('');
    const [assignSubmitting, setAssignSubmitting] = useState(false);
    const [isRemoveAssignOpen, setIsRemoveAssignOpen] = useState(false);
    const [removeMarkDeactivated, setRemoveMarkDeactivated] = useState(false);
    const [removeReasonId, setRemoveReasonId] = useState('');
    const [removeNotes, setRemoveNotes] = useState('');
    const [deactivationReasons, setDeactivationReasons] = useState([]);
    const [deactivationReasonsLoading, setDeactivationReasonsLoading] = useState(true);
    const [deactivationReasonsError, setDeactivationReasonsError] = useState('');
    const [removeSubmitting, setRemoveSubmitting] = useState(false);
    // Drilldown colapsável (pedido d)  começa tudo colapsado.
    const [expandedOTs, setExpandedOTs] = useState(() => new Set());
    const [expandedBatches, setExpandedBatches] = useState(() => new Set());
    const toggleOT = (id) => setExpandedOTs(prev => {
        const n = new Set(prev);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });
    const toggleBatch = (id) => setExpandedBatches(prev => {
        const n = new Set(prev);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });

    // Painel lateral de detalhe do contacto externo + histórico de notificações
    const [contactPanel, setContactPanel] = useState(null); // { contact, tagId } | null

    // "Solicitar Tags Novas" modal
    const [isRequestOpen, setIsRequestOpen] = useState(false);
    const [objectTypes, setObjectTypes] = useState([]);
    const [objectTypesLoading, setObjectTypesLoading] = useState(true);
    const [objectTypesError, setObjectTypesError] = useState('');
    const [tagBatches, setTagBatches] = useState([]);
    const [tagBatchesLoading, setTagBatchesLoading] = useState(true);
    const [tagBatchesError, setTagBatchesError] = useState('');
    const [reqObjectTypeId, setReqObjectTypeId] = useState('');
    const [reqTemplateId, setReqTemplateId] = useState('');
    const [reqMultiplier, setReqMultiplier] = useState(0);
    const [reqMultiplierOptions, setReqMultiplierOptions] = useState([]);
    const [reqConfigLoading, setReqConfigLoading] = useState(false);
    const [reqConfigError, setReqConfigError] = useState('');
    const [reqPackSize, setReqPackSize] = useState(0);
    const [reqDesiredBy, setReqDesiredBy] = useState('');
    const [reqJustification, setReqJustification] = useState('');
    const [reqNotes, setReqNotes] = useState('');
    const [reqSubmitting, setReqSubmitting] = useState(false);

    const visibleObjectTypes = useMemo(
        () => objectTypes.filter(objectType => !hideChargersInfo || !isChargerObjectType(objectType)),
        [objectTypes]
    );
    const availableObjectTypes = visibleObjectTypes;
    const selectedRequestObjectType = availableObjectTypes.find(o => String(o.id) === String(reqObjectTypeId));
    const reqTemplateOptions = selectedRequestObjectType?.templates || [];
    const reqTotalQuantity = reqPackSize * reqMultiplier;
    const tagPermissions = {
        create: can(TAG_ACTION_PERMISSIONS.create),
        read: can(TAG_ACTION_PERMISSIONS.read),
        update: can(TAG_ACTION_PERMISSIONS.update),
        delete: can(TAG_ACTION_PERMISSIONS.delete),
    };
    const tagRequestPermissions = {
        create: can(TAG_REQUEST_ACTION_PERMISSIONS.create),
        read: can(TAG_REQUEST_ACTION_PERMISSIONS.read),
        update: can(TAG_REQUEST_ACTION_PERMISSIONS.update),
        delete: can(TAG_REQUEST_ACTION_PERMISSIONS.delete),
    };
    const canReadTags = tagPermissions.read;
    const canAssignTags = tagPermissions.update;
    const canReadTagRequestConfig = tagRequestPermissions.read;
    const canCreateTagRequests = tagRequestPermissions.create;

    useEffect(() => {
        let cancelled = false;

        const loadTags = async () => {
            if (!canReadTags) {
                setTags([]);
                setTagsError('Nao tem permissao para consultar tags.');
                setTagsLoading(false);
                return;
            }

            setTagsLoading(true);
            setTagsError('');

            try {
                const response = await api.get(tagEndpoints.list);
                if (!cancelled) setTags(normalizeTags(response.data));
            } catch (error) {
                if (!cancelled) {
                    setTags([]);
                    setTagsError(getApiErrorMessage(error, 'Não foi possível carregar as tags.'));
                }
            } finally {
                if (!cancelled) setTagsLoading(false);
            }
        };

        loadTags();

        return () => {
            cancelled = true;
        };
    }, [canReadTags]);

    useEffect(() => {
        let cancelled = false;

        const loadResources = async () => {
            if (!canAssignTags) {
                setResources([]);
                setResourcesError('');
                setResourcesLoading(false);
                return;
            }

            setResourcesLoading(true);
            setResourcesError('');

            try {
                const response = await api.get(resourceEndpoints.list);
                if (!cancelled) setResources(normalizeAssignableResources(response.data));
            } catch (error) {
                if (!cancelled) {
                    setResources([]);
                    setResourcesError(getApiErrorMessage(error, 'Nao foi possível carregar os recursos.'));
                }
            } finally {
                if (!cancelled) setResourcesLoading(false);
            }
        };

        loadResources();

        return () => {
            cancelled = true;
        };
    }, [canAssignTags]);

    useEffect(() => {
        let cancelled = false;

        const loadDeactivationReasons = async () => {
            if (!canAssignTags) {
                setDeactivationReasons([]);
                setDeactivationReasonsError('');
                setDeactivationReasonsLoading(false);
                return;
            }

            setDeactivationReasonsLoading(true);
            setDeactivationReasonsError('');

            try {
                const response = await api.get(tagDeactivationReasonEndpoints.list);
                if (!cancelled) setDeactivationReasons(normalizeDeactivationReasons(response.data));
            } catch (error) {
                if (!cancelled) {
                    setDeactivationReasons([]);
                    setDeactivationReasonsError(getApiErrorMessage(error, 'Nao foi possível carregar os motivos de desativação.'));
                }
            } finally {
                if (!cancelled) setDeactivationReasonsLoading(false);
            }
        };

        loadDeactivationReasons();

        return () => {
            cancelled = true;
        };
    }, [canAssignTags]);

    useEffect(() => {
        let cancelled = false;

        const loadTagBatches = async () => {
            if (!canReadTags) {
                setTagBatches([]);
                setTagBatchesError('');
                setTagBatchesLoading(false);
                return;
            }

            setTagBatchesLoading(true);
            setTagBatchesError('');

            try {
                const batches = await fetchTagBatches();
                if (!cancelled) setTagBatches(batches);
            } catch (error) {
                if (!cancelled) {
                    setTagBatches([]);
                    setTagBatchesError(getApiErrorMessage(error, 'Não foi possível carregar os lotes de tags.'));
                }
            } finally {
                if (!cancelled) setTagBatchesLoading(false);
            }
        };

        loadTagBatches();

        return () => {
            cancelled = true;
        };
    }, [canReadTags]);

    useEffect(() => {
        let cancelled = false;

        const loadObjectTypes = async () => {
            if (!canReadTags && !canReadTagRequestConfig) {
                setObjectTypes([]);
                setObjectTypesError('');
                setObjectTypesLoading(false);
                return;
            }

            setObjectTypesLoading(true);
            setObjectTypesError('');

            try {
                const response = await api.get(objectTypeEndpoints.list);
                if (!cancelled) setObjectTypes(normalizeObjectTypes(response.data));
            } catch (error) {
                if (!cancelled) {
                    setObjectTypes([]);
                    setObjectTypesError(getApiErrorMessage(error, 'Não foi possível carregar os tipos de objeto.'));
                }
            } finally {
                if (!cancelled) setObjectTypesLoading(false);
            }
        };

        loadObjectTypes();

        return () => {
            cancelled = true;
        };
    }, [canReadTags, canReadTagRequestConfig]);

    useEffect(() => {
        if (!reqObjectTypeId) {
            setReqTemplateId('');
            setReqMultiplier(0);
            setReqMultiplierOptions([]);
            setReqPackSize(0);
            setReqConfigError('');
            return;
        }
        if (!canReadTagRequestConfig) {
            setReqMultiplier(0);
            setReqMultiplierOptions([]);
            setReqPackSize(0);
            setReqConfigError('Nao tem permissão para consultar a configuração de pedidos de tags.');
            return;
        }

        let cancelled = false;

        const loadTagRequestConfig = async () => {
            setReqConfigLoading(true);
            setReqConfigError('');

            try {
                const response = await api.get(objectTypeEndpoints.tagRequestConfig(reqObjectTypeId));
                const source = response.data?.data ?? response.data;
                const selectedObjectType = availableObjectTypes.find(o => String(o.id) === String(reqObjectTypeId));
                const templates = selectedObjectType?.templates || [];
                const currentTemplate = templates.find(template => String(template.id) === String(reqTemplateId)) || templates[0];
                const packSize = packSizeFrom(currentTemplate) || packSizeFrom(source) || selectedObjectType?.pack_size || 0;
                const multipliers = multiplierOptionsFrom(currentTemplate).length
                    ? multiplierOptionsFrom(currentTemplate)
                    : multiplierOptionsFrom(source).length
                        ? multiplierOptionsFrom(source)
                        : multiplierOptionsFrom(selectedObjectType);

                if (!cancelled) {
                    setReqTemplateId(currentTemplate ? String(currentTemplate.id) : '');
                    setReqPackSize(packSize);
                    setReqMultiplierOptions(multipliers);
                    setReqMultiplier(multipliers[0] || 0);
                }
            } catch (error) {
                if (!cancelled) {
                    setReqPackSize(0);
                    setReqMultiplierOptions([]);
                    setReqMultiplier(0);
                    setReqConfigError(getApiErrorMessage(error, 'Não foi possível carregar a quantidade para este tipo de objeto.'));
                }
            } finally {
                if (!cancelled) setReqConfigLoading(false);
            }
        };

        loadTagRequestConfig();

        return () => {
            cancelled = true;
        };
    }, [reqObjectTypeId, reqTemplateId, availableObjectTypes, canReadTagRequestConfig]);

    const handleRequestObjectTypeChange = (objectTypeId) => {
        const objectType = availableObjectTypes.find(o => String(o.id) === String(objectTypeId));
        const defaultTemplate = objectType?.templates?.[0];
        setReqObjectTypeId(objectTypeId);
        setReqTemplateId(defaultTemplate ? String(defaultTemplate.id) : '');
    };

    const handleOpenRequest = () => {
        if (!canCreateTagRequests) {
            toast.error('Nao tem permissão para criar pedidos de tags.');
            return;
        }
        if (!canReadTagRequestConfig) {
            toast.error('Nao tem permissão para consultar a configuração de pedidos de tags.');
            return;
        }

        setReqObjectTypeId('');
        setReqTemplateId('');
        setReqMultiplier(0);
        setReqMultiplierOptions([]);
        setReqPackSize(0);
        setReqConfigError('');
        setReqDesiredBy('');
        setReqJustification('');
        setReqNotes('');
        setReqSubmitting(false);
        setIsRequestOpen(true);
    };

    const handleSubmitRequest = async () => {
        if (!canCreateTagRequests) {
            toast.error('Nao tem permissão para criar pedidos de tags.');
            return;
        }

        if (!reqObjectTypeId) {
            toast.error('Selecione o tipo de objeto.');
            return;
        }
        if (!reqTemplateId) {
            toast.error('Selecione o modelo.');
            return;
        }
        if (!reqMultiplier || reqMultiplier < 1) {
            toast.error('Selecione um múltiplo do pack.');
            return;
        }
        if (!reqPackSize || reqPackSize < 1) {
            toast.error('Não foi possível obter o pack deste tipo de objeto.');
            return;
        }
        if (!reqJustification.trim()) {
            toast.error('A justificação é obrigatória.');
            return;
        }
        const objType = availableObjectTypes.find(o => String(o.id) === String(reqObjectTypeId));

        setReqSubmitting(true);
        try {
            await api.post(tagRequestEndpoints.create, {
                object_type_id: reqObjectTypeId,
                object_type_template_id: toApiId(reqTemplateId),
                multiplier: reqMultiplier,
                pack_size: reqPackSize,
                quantity: reqTotalQuantity,
                desired_by: reqDesiredBy || null,
                justification: reqJustification.trim(),
                notes: reqNotes.trim() || null,
            });

            setIsRequestOpen(false);
            toast.success(`Pedido de ${reqTotalQuantity} tags (${reqMultiplier}× pack de ${reqPackSize}  ${objType?.name}) submetido. Aguarde aprovação do Gestor do Sistema.`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Não foi possível submeter o pedido de tags.'));
        } finally {
            setReqSubmitting(false);
        }
    };

    const tagBatchById = new Map();
    tagBatches.forEach(batch => {
        [
            batch.id,
            batch.batch_id,
            batch.tag_batch_id,
            batch.gest_tag_batch_id,
            batch.gest_tag_batches_id,
            batch.code,
            batch.reference,
        ].filter(value => value !== undefined && value !== null && value !== '').forEach(value => {
            tagBatchById.set(String(value), batch);
        });
    });
    const tagsWithMetadata = tags.map(tag => {
        const batch = tagBatchById.get(String(tag.batchId));
        const objectTypeId = tag.objectTypeId ?? batch?.objectTypeId ?? null;
        const objectType = objectTypes.find(type => String(type.id) === String(objectTypeId));
        const tagObjectType = tag.objectType && tag.objectType !== 'Sem tipo de objeto' ? tag.objectType : '';
        const objectTypeName = objectType?.name || objectType?.title || batch?.objectTypeName || tagObjectType || (objectTypeId ? `Tipo ${objectTypeId}` : 'Sem tipo de objeto');

        return {
            ...tag,
            objectTypeId,
            objectType: objectTypeName,
            hiddenAsCharger: hideChargersInfo && (
                isChargerObjectType(objectType)
                || isChargerInfo(batch?.objectTypeName, batch?.object_type_name, tagObjectType, objectTypeName)
            ),
        };
    }).filter(tag => !tag.hiddenAsCharger);
    const categoryOptions = Array.from(new Set(tagsWithMetadata.map(tag => tag.category).filter(category => category && category !== '-')));
    const statusOptions = Array.from(new Map(
        tagsWithMetadata
            .filter(tag => tag.status)
            .map(tag => [tag.status, tag.statusLabel || tag.status])
    ).entries()).map(([value, label]) => ({value, label}));

    const filteredTags = tagsWithMetadata.filter(tag => {
        const matchesCategory = categoryFilter === 'all' || tag.category === categoryFilter;
        const matchesStatus = statusFilter === 'all' || tag.status === statusFilter;
        const q = searchQuery.toLowerCase();
        const matchesSearch = String(tag.id || '').toLowerCase().includes(q) ||
            String(tag.resourceReference || '').toLowerCase().includes(q) ||
            String(tag.resourceName || '').toLowerCase().includes(q) ||
            String(tag.resourceType || '').toLowerCase().includes(q) ||
            String(tag.category || '').toLowerCase().includes(q) ||
            String(tag.objectType || '').toLowerCase().includes(q) ||
            String(tag.vehicle || '').toLowerCase().includes(q);
        return matchesCategory && matchesStatus && matchesSearch;
    });

    // Build grouped structure: by objectType, then by batch
    const groupedTags = (() => {
        if (groupBy === 'none') return null;
        const otMap = new Map();
        filteredTags.forEach(t => {
            const k = t.objectTypeId ?? 0;
            if (!otMap.has(k)) {
                const ot = visibleObjectTypes.find(o => String(o.id) === String(t.objectTypeId));
                const tagObjectType = t.objectType && t.objectType !== 'Sem tipo de objeto' ? t.objectType : '';
                otMap.set(k, {
                    objectTypeId: k,
                    objectTypeName: ot?.name || ot?.title || ot?.attributes?.title || tagObjectType || (k && k !== 0 ? `Tipo ${k}` : 'Sem tipo de objeto'),
                    objectTypeCategory: ot?.category || '',
                    tags: [],
                    batches: new Map(),
                });
            }
            otMap.get(k).tags.push(t);
        });
        if (groupBy === 'objectType-batch') {
            otMap.forEach(g => {
                g.tags.forEach(t => {
                    const bk = t.batchId ?? 0;
                    if (!g.batches.has(bk)) {
                        const b = tagBatchById.get(String(t.batchId));
                        g.batches.set(bk, {
                            batchId: bk,
                            batchCode: b?.code || (tagBatchesLoading && bk ? 'A carregar lote...' : (bk ? `Lote #${bk}` : 'Sem lote')),
                            batchData: b,
                            tags: [],
                        });
                    }
                    g.batches.get(bk).tags.push(t);
                });
            });
        }
        return Array.from(otMap.values()).map(g => ({
            ...g,
            batches: Array.from(g.batches.values()),
        }));
    })();

    const assignedIdentifiers = new Set(tagsWithMetadata.filter(t => t.status === 'atribuida').map(t => t.resourceReference));
    const availableObjects = resources.filter(resource => !assignedIdentifiers.has(resource.identifier) && !resource.hasTag);
    const activeDeactivationReasons = deactivationReasons.filter(reason => Number(reason.status) === 1);

    const handleOpenAssign = (tag) => {
        if (!canAssignTags) {
            toast.error('Nao tem permissao para atualizar tags.');
            return;
        }

        setSelectedTag(tag);
        setAssignTargetReference('');
        setAssignSubmitting(false);
        setIsAssignOpen(true);
    };

    const handleOpenRemoveAssignment = (tag) => {
        if (!canAssignTags) {
            toast.error('Nao tem permissao para atualizar tags.');
            return;
        }

        setSelectedTag(tag);
        setRemoveMarkDeactivated(false);
        setRemoveReasonId('');
        setRemoveNotes('');
        setRemoveSubmitting(false);
        setIsRemoveAssignOpen(true);
    };

    const handleAssignToVehicle = async () => {
        if (!canAssignTags) {
            toast.error('Nao tem permissao para atualizar tags.');
            return;
        }

        if (!selectedTag) {
            toast.error('Tag não encontrada.');
            return;
        }
        if (!assignTargetReference) {
            toast.error('Selecione um objeto (veículo ou outro).');
            return;
        }
        const target = availableObjects.find(o => o.uid === assignTargetReference);
        if (!target) {
            toast.error('Objeto não encontrado.');
            return;
        }

        setAssignSubmitting(true);
        try {
            const response = await api.post(tagEndpoints.assign, assignTagPayload(selectedTag, target));
            const responseTag = normalizeTag(response.data?.data ?? response.data?.tag ?? response.data);
            const updatedTag = responseTag.id ? responseTag : {
                ...selectedTag,
                status: 'atribuida',
                resourceReference: target.identifier,
                resourceName: target.resourceName,
                resourceType: target.resourceType,
                category: target.category || '-',
                objectType: target.objectType || selectedTag.objectType,
                vehicle: target.label,
                location: target.type === 'veiculo' ? 'Vidro Frontal' : (target.category || ''),
            };

            setTags(prev => prev.map(t => t.id === selectedTag.id ? updatedTag : t));
            setResources(prev => prev.map(resource => (
                resource.uid === target.uid ? {...resource, hasTag: true} : resource
            )));
            setIsAssignOpen(false);
            toast.success(`Tag ${selectedTag.id} atribuída a ${target.label} (${target.identifier})!`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Não foi possível atribuir a tag ao objeto.'));
        } finally {
            setAssignSubmitting(false);
        }
    };

    const handleRemoveAssignment = async () => {
        if (!canAssignTags) {
            toast.error('Nao tem permissão para atualizar tags.');
            return;
        }

        if (!selectedTag) {
            toast.error('Tag nao encontrada.');
            return;
        }

        if (!selectedTag.resourceId) {
            toast.error('Esta tag nao tem objeto atribuído.');
            return;
        }

        if (removeMarkDeactivated && !removeReasonId) {
            toast.error('Selecione o motivo da desativação.');
            return;
        }

        if (removeMarkDeactivated && activeDeactivationReasons.length === 0) {
            toast.error(deactivationReasonsError || 'Nao existem motivos de desativação ativos.');
            return;
        }

        const previousResourceId = selectedTag.resourceId;
        const previousReference = selectedTag.resourceReference;

        setRemoveSubmitting(true);
        try {
            const response = await api.post(tagEndpoints.removeAssignment, removeTagAssignmentPayload(selectedTag, removeMarkDeactivated, removeReasonId, removeNotes));
            const responseTag = normalizeTag(response.data?.data ?? response.data?.tag ?? response.data);
            const fallbackStatus = removeMarkDeactivated ? 'inativa' : 'armazem';
            const updatedTag = {
                ...(responseTag.id ? responseTag : selectedTag),
                status: responseTag.id ? (responseTag.status || fallbackStatus) : fallbackStatus,
                resourceId: null,
                vehicle: '-',
                resourceReference: '-',
                user: '-',
                location: '-',
                updatedAt: (responseTag.id ? responseTag.updatedAt : '') || new Date().toISOString().slice(0, 10),
            };

            setTags(prev => prev.map(t => t.id === selectedTag.id ? updatedTag : t));
            setResources(prev => prev.map(resource => {
                const sameResource = String(resource.id ?? resource.uid) === String(previousResourceId)
                    || String(resource.identifier) === String(previousReference);
                return sameResource ? {...resource, hasTag: false} : resource;
            }));
            setIsRemoveAssignOpen(false);
            toast.success(removeMarkDeactivated ? `Tag ${selectedTag.id} removida e desativada.` : `Atribuição da tag ${selectedTag.id} removida.`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Nao foi possível remover a atribuição da tag.'));
        } finally {
            setRemoveSubmitting(false);
        }
    };

    // Stats
    const totalTags = tagsWithMetadata.length;
    const assignedTags = tagsWithMetadata.filter(t => t.status === 'atribuida').length;
    const warehouseTags = tagsWithMetadata.filter(t => t.status === 'armazem').length;
    const inactiveTags = tagsWithMetadata.filter(t => t.status === 'inativa').length;

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Gestão de Tags</h1>
                        <p className="text-slate-600 font-primary mt-1">Tags QR associadas aos veículos da frota</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {canCreateTagRequests && (
                            <Button onClick={handleOpenRequest} className="bg-blue-600 hover:bg-blue-700" data-testid="request-tags-btn">
                                <Send size={16} className="mr-2"/>Solicitar Tags Novas
                            </Button>
                        )}
                        <Button variant="outline" data-testid="export-tags-btn"><Download size={18} className="mr-2"/>Exportar</Button>
                    </div>
                </div>

                {/* Info banner  Tags são criadas no painel Admin */}
                <Card className="p-4 border-blue-200 bg-blue-50 flex items-start gap-3">
                    <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={18}/>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900">As tags são emitidas pelo Gestor do Sistema do sistema em <strong>lotes</strong>.</p>
                        <p className="text-xs text-blue-700 mt-0.5">Aqui pode consultar, atribuir tags em armazém e <strong>solicitar tags novas</strong> através do botão acima.</p>
                    </div>
                </Card>

                {tagBatchesError && (
                    <Card className="p-3 border-amber-200 bg-amber-50 text-xs text-amber-800">
                        {tagBatchesError}
                    </Card>
                )}

                {tagsError && (
                    <Card className="p-3 border-red-200 bg-red-50 text-xs text-red-700">
                        {tagsError}
                    </Card>
                )}

                {resourcesError && (
                    <Card className="p-3 border-amber-200 bg-amber-50 text-xs text-amber-800">
                        {resourcesError}
                    </Card>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-1"><p className="text-slate-600 text-xs font-medium">Total Tags</p><QrCode className="text-blue-600" size={18}/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{totalTags}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-1"><p className="text-slate-600 text-xs font-medium">Em Veiculos</p><Car className="text-emerald-600" size={18}/></div>
                        <p className="text-2xl font-secondary font-bold text-emerald-700">{assignedTags}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-1"><p className="text-slate-600 text-xs font-medium">Em Armazém</p><Package className="text-amber-600" size={18}/></div>
                        <p className="text-2xl font-secondary font-bold text-amber-700">{warehouseTags}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-1"><p className="text-slate-600 text-xs font-medium">Inativas</p><Ban className="text-red-600" size={18}/></div>
                        <p className="text-2xl font-secondary font-bold text-red-700">{inactiveTags}</p>
                    </Card>
                </div>

                <Card>
                    <div className="p-4 border-b border-slate-200">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 max-w-md relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16}/>
                                <Input placeholder="Pesquisar por ID, nome, tipo, categoria ou referência..." className="pl-9" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}/>
                            </div>
                            <div className="flex items-center gap-3">
                                <Select value={groupBy} onValueChange={setGroupBy}>
                                    <SelectTrigger className="w-[200px]" data-testid="group-by-select">
                                        <Layers size={14} className="mr-1 text-slate-500"/>
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="objectType-batch">Tipo de objeto / Lote</SelectItem>
                                        <SelectItem value="objectType">Por tipo de objeto</SelectItem>
                                        <SelectItem value="none">Sem agrupamento (lista plana)</SelectItem>
                                    </SelectContent>
                                </Select>
                                {groupBy !== 'none' && groupedTags && (
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setExpandedOTs(new Set(groupedTags.map(g => g.objectTypeId)));
                                                setExpandedBatches(new Set(groupedTags.flatMap(g => (g.batches || []).map(b => b.batchId))));
                                            }}
                                            data-testid="expand-all-btn"
                                            className="h-9"
                                        >
                                            Expandir tudo
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setExpandedOTs(new Set());
                                                setExpandedBatches(new Set());
                                            }}
                                            data-testid="collapse-all-btn"
                                            className="h-9"
                                        >
                                            Colapsar
                                        </Button>
                                    </div>
                                )}
                                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                                    <SelectTrigger className="w-[170px]"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas Categorias</SelectItem>
                                        {categoryOptions.map(category => (
                                            <SelectItem key={category} value={category}>{category}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-[150px]"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos Estados</SelectItem>
                                        {statusOptions.map(option => (
                                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <div className="border-l border-slate-200 pl-3 flex gap-1">
                                    <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-100'}`}><List size={18}/></button>
                                    <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-100'}`}><Grid3x3 size={18}/></button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {viewMode === 'list' ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>ID da Tag</TableHead>
                                    <TableHead>Objecto</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead>Matrícula / Referência</TableHead>
                                    <TableHead>Nome</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Categoria</TableHead>
                                    <TableHead>Condutor</TableHead>
                                    <TableHead>Datas</TableHead>
                                    <TableHead>Última Ocorrência</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {tagsLoading && (
                                    <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">A carregar tags...</TableCell></TableRow>
                                )}

                                {!tagsLoading && groupBy === 'none' && filteredTags.map((tag) => (
                                    <TagRow key={tag.id} tag={tag} onAssign={handleOpenAssign} onRemoveAssignment={handleOpenRemoveAssignment} onAvatarClick={setContactPanel} canAssign={canAssignTags}/>
                                ))}

                                {!tagsLoading && groupBy !== 'none' && groupedTags && groupedTags.map((g) => (
                                    <GroupRows
                                        key={'ot-' + g.objectTypeId}
                                        group={g}
                                        showBatch={groupBy === 'objectType-batch'}
                                        onAssign={handleOpenAssign}
                                        onRemoveAssignment={handleOpenRemoveAssignment}
                                        onAvatarClick={setContactPanel}
                                        canAssign={canAssignTags}
                                        expandedOTs={expandedOTs}
                                        expandedBatches={expandedBatches}
                                        onToggleOT={toggleOT}
                                        onToggleBatch={toggleBatch}
                                    />
                                ))}

                                {!tagsLoading && groupBy === 'none' && filteredTags.length === 0 && (
                                    <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">Nenhuma tag corresponde aos filtros.</TableCell></TableRow>
                                )}

                                {!tagsLoading && groupBy !== 'none' && groupedTags && groupedTags.length === 0 && (
                                    <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">Nenhuma tag corresponde aos filtros.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {tagsLoading && (
                                <div className="col-span-full text-center text-slate-400 py-8">A carregar tags...</div>
                            )}

                            {!tagsLoading && filteredTags.map((tag) => (
                                <div key={tag.id} className="bg-slate-50 border border-slate-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group">
                                    <div className="flex items-start justify-between mb-3">
                                        <span className="font-mono font-bold text-slate-900">{tag.id}</span>
                                        <StatusBadgeCustom tag={tag}/>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-slate-500">Objecto:</span><span className="font-medium text-slate-900">{tag.objectType}</span>
                                        </div>
                                        {tag.resourceName !== '-' && <div className="flex justify-between"><span className="text-slate-500">Nome:</span><span className="font-medium text-slate-900">{tag.resourceName}</span></div>}
                                        {tag.resourceType !== '-' && <div className="flex justify-between"><span className="text-slate-500">Tipo:</span><span className="font-medium text-slate-900">{tag.resourceType}</span></div>}
                                        {tag.category !== '-' && <div className="flex justify-between"><span className="text-slate-500">Categoria:</span><span className="font-medium text-slate-900">{tag.category}</span></div>}
                                        {tag.resourceReference !== '-' && <div className="flex justify-between"><span className="text-slate-500">Referência:</span><Badge variant="outline" className="font-mono text-xs">{tag.resourceReference}</Badge></div>}
                                        {tag.user !== '-' && <div className="flex justify-between"><span className="text-slate-500">Condutor:</span><span className="font-medium text-slate-900">{tag.user}</span></div>}
                                    </div>
                                    {canAssignTags && tag.status === 'armazem' && (
                                        <Button variant="outline" size="sm" className="w-full mt-3 text-blue-600 border-blue-200" onClick={() => handleOpenAssign(tag)}>
                                            <Car size={14} className="mr-1"/>Atribuir a Veículo
                                        </Button>
                                    )}
                                    {canAssignTags && tag.status === 'atribuida' && (
                                        <Button variant="outline" size="sm" className="w-full mt-3 text-red-600 border-red-200 hover:text-red-700" onClick={() => handleOpenRemoveAssignment(tag)}>
                                            <Ban size={14} className="mr-1"/>Remover atribuição
                                        </Button>
                                    )}
                                </div>
                            ))}

                            {!tagsLoading && filteredTags.length === 0 && (
                                <div className="col-span-full text-center text-slate-400 py-8">Nenhuma tag corresponde aos filtros.</div>
                            )}
                        </div>
                    )}

                    <div className="p-4 border-t border-slate-200 flex items-center justify-between text-sm text-slate-600">
                        <span>A mostrar {filteredTags.length} de {tagsWithMetadata.length} tags</span>
                    </div>
                </Card>

                {/* Assign Tag to Object Dialog */}
                <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2"><Car className="text-blue-600" size={20}/>Atribuir Tag a Objeto</DialogTitle>
                            <DialogDescription>Associar a tag {selectedTag?.id} a um objeto já registado (veículo ou outro equipamento)</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            {selectedTag && (
                                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                                    <span className="font-mono font-bold text-lg text-purple-900">{selectedTag.id}</span>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label>Objeto a atribuir *</Label>
                                <Select value={assignTargetReference} onValueChange={setAssignTargetReference} disabled={assignSubmitting}>
                                    <SelectTrigger data-testid="assign-target-select">
                                        <SelectValue placeholder="Selecione um objeto da frota..."/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {resourcesLoading ? (
                                            <div className="p-3 text-xs text-slate-500 text-center">
                                                A carregar recursos...
                                            </div>
                                        ) : availableObjects.length === 0 ? (
                                            <div className="p-3 text-xs text-slate-500 text-center">
                                                Todos os objetos já tem tag atribuida.
                                            </div>
                                        ) : availableObjects.map(obj => (
                                                <SelectItem key={obj.uid} value={obj.uid}>
                                                    <div className="flex items-center gap-2">
                                                    {obj.objectIcon ? (
                                                        <img src={obj.objectIcon} alt="" className="h-3.5 w-3.5 object-contain" />
                                                    ) : (
                                                        <Car size={14} className="text-slate-500"/>
                                                    )}
                                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{obj.objectType || obj.type}</Badge>
                                                        <span className="text-slate-600"> {obj.identifier}</span>
                                                    </div>
                                                </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-slate-500">Objetos registados: veículos e outros equipamentos. Apenas os sem tag são listados.</p>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsAssignOpen(false)} disabled={assignSubmitting}>Cancelar</Button>
                            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleAssignToVehicle} disabled={!canAssignTags || !assignTargetReference || resourcesLoading || assignSubmitting} data-testid="confirm-assign-btn">
                                <Car size={16} className="mr-2"/>{assignSubmitting ? 'A atribuir...' : 'Atribuir'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Remove Tag Assignment Dialog */}
                <Dialog open={isRemoveAssignOpen} onOpenChange={setIsRemoveAssignOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2"><Ban className="text-red-600" size={20}/>Remover atribuição</DialogTitle>
                            <DialogDescription>Remover a associação da tag {selectedTag?.id} ao objeto atual.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            {selectedTag && (
                                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="font-mono font-bold text-lg text-slate-900">{selectedTag.id}</span>
                                        {selectedTag.resourceReference !== '-' && <Badge variant="outline" className="font-mono">{selectedTag.resourceReference}</Badge>}
                                    </div>
                                    {selectedTag.vehicle !== '-' && <p className="text-sm text-slate-600 mt-1">{selectedTag.vehicle}</p>}
                                </div>
                            )}

                            <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer">
                                <Checkbox
                                    checked={removeMarkDeactivated}
                                    onCheckedChange={(checked) => setRemoveMarkDeactivated(Boolean(checked))}
                                    disabled={removeSubmitting}
                                    data-testid="remove-deactivate-checkbox"
                                />
                                <span className="space-y-1">
                                    <span className="block text-sm font-medium text-slate-900">Marcar tag como inativa</span>
                                    <span className="block text-xs text-slate-500">Se não marcar esta opção, a tag volta para armazém e pode ser atribuída novamente.</span>
                                </span>
                            </label>

                            {removeMarkDeactivated && (
                                <>
                                    <div className="space-y-2">
                                        <Label>Motivo da desativação *</Label>
                                        <Select value={removeReasonId} onValueChange={setRemoveReasonId} disabled={removeSubmitting || deactivationReasonsLoading || activeDeactivationReasons.length === 0}>
                                            <SelectTrigger data-testid="remove-deactivation-reason">
                                                <SelectValue placeholder={deactivationReasonsLoading ? 'A carregar motivos...' : 'Selecione um motivo...'}/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {activeDeactivationReasons.map(reason => (
                                                    <SelectItem key={reason.id} value={String(reason.id)}>{reason.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {!deactivationReasonsLoading && (deactivationReasonsError || activeDeactivationReasons.length === 0) && (
                                            <p className="text-xs text-red-600">{deactivationReasonsError || 'Sem motivos ativos disponiveis.'}</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Notas</Label>
                                        <Textarea
                                            rows={3}
                                            value={removeNotes}
                                            onChange={(event) => setRemoveNotes(event.target.value)}
                                            placeholder="Notas internas sobre a remoção ou desativação..."
                                            disabled={removeSubmitting}
                                            data-testid="remove-deactivation-notes"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsRemoveAssignOpen(false)} disabled={removeSubmitting}>Cancelar</Button>
                            <Button variant="destructive" onClick={handleRemoveAssignment} disabled={!canAssignTags || removeSubmitting || (removeMarkDeactivated && (!removeReasonId || deactivationReasonsLoading || activeDeactivationReasons.length === 0))} data-testid="confirm-remove-assignment-btn">
                                <Ban size={16} className="mr-2"/>{removeSubmitting ? 'A remover...' : 'Remover atribuição'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Solicitar Tags Novas Dialog */}
                <Dialog open={isRequestOpen} onOpenChange={setIsRequestOpen}>
                    <DialogContent className="max-w-lg" data-testid="request-tags-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2"><Send className="text-blue-600" size={20}/>Solicitar Tags Novas</DialogTitle>
                            <DialogDescription>O Gestor do Sistema do sistema receberá o pedido. Após aprovação, será criado um novo lote de tags para a sua empresa.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5 col-span-2">
                                    <Label>Tipo de Objeto *</Label>
                                    <Select value={reqObjectTypeId} onValueChange={handleRequestObjectTypeChange} disabled={objectTypesLoading}>
                                        <SelectTrigger data-testid="request-object-type">
                                            <SelectValue placeholder={objectTypesLoading ? 'A carregar tipos...' : availableObjectTypes.length ? 'Para que tipo de objeto?' : 'Sem tipos ativos para esta empresa'}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {objectTypesError && (
                                                <div className="p-3 text-xs text-red-600">{objectTypesError}</div>
                                            )}
                                            {availableObjectTypes.length === 0 && (
                                                <div className="p-3 text-xs text-slate-500">Nenhum tipo de objeto ativo. Contacte o Gestor do Sistema.</div>
                                            )}
                                            {availableObjectTypes.map(o => (
                                                <SelectItem key={o.id} value={String(o.id)}>
                                                    {o.name}{o.pack_size ? <span className="text-slate-400"> · pack de {o.pack_size}</span> : null}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-1.5 col-span-2">
                                    <Label>Modelo *</Label>
                                    {reqObjectTypeId ? (
                                        <Select value={reqTemplateId} onValueChange={setReqTemplateId} disabled={reqTemplateOptions.length === 0}>
                                            <SelectTrigger data-testid="request-object-type-template">
                                                <SelectValue placeholder={reqTemplateOptions.length ? 'Selecionar modelo' : 'Sem modelos disponiveis'}/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {reqTemplateOptions.length === 0 ? (
                                                    <div className="p-3 text-xs text-slate-500">Nenhum modelo disponivel para este tipo de objeto.</div>
                                                ) : reqTemplateOptions.map(template => (
                                                    <SelectItem key={template.id} value={String(template.id)}>
                                                        {template.name}{template.pack_size ? <span className="text-slate-400"> - pack de {template.pack_size}</span> : null}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <div className="h-10 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-xs text-slate-400">
                                            Selecione primeiro o tipo de objeto
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-1.5">
                                    <Label>Quantidade *</Label>
                                    {reqObjectTypeId ? (
                                        <>
                                            <Select value={reqMultiplier ? String(reqMultiplier) : ''} onValueChange={(v) => setReqMultiplier(parseInt(v, 10))} disabled={reqConfigLoading || reqMultiplierOptions.length <= 1}>
                                                <SelectTrigger data-testid="request-multiplier">
                                                    <SelectValue placeholder={reqConfigLoading ? 'A carregar quantidade...' : 'Quantidade indisponível'}/>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {reqMultiplierOptions.map(m => (
                                                        <SelectItem key={m} value={String(m)}>
                                                            {m}× pack · <strong className="font-mono">{m * reqPackSize} tags</strong>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <p className="text-[11px] text-slate-500">
                                                Pack da empresa: <strong>{reqPackSize}</strong> tag{reqPackSize === 1 ? '' : 's'} por unidade. Total: <strong className="text-blue-700">{reqTotalQuantity} tags</strong>.
                                            </p>
                                            {reqConfigError && <p className="text-[11px] text-red-600">{reqConfigError}</p>}
                                        </>
                                    ) : (
                                        <div className="h-10 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-xs text-slate-400">
                                            Selecione primeiro o tipo de objeto
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Pretendido até</Label>
                                    <Input type="date" value={reqDesiredBy} onChange={e => setReqDesiredBy(e.target.value)}/>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Justificação *</Label>
                                <Textarea rows={3} value={reqJustification} onChange={e => setReqJustification(e.target.value)} placeholder="Ex: Aquisição de novos veículos para a frota; substituição de tags danificadas..." data-testid="request-justification"/>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Notas adicionais (opcional)</Label>
                                <Textarea rows={2} value={reqNotes} onChange={e => setReqNotes(e.target.value)} placeholder="Outras informações úteis para o Gestor do Sistema."/>
                            </div>
                            <div className="flex items-start gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                                <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0"/>
                                <span>As tags são emitidas pelo Gestor do Sistema em packs definidos por tipo de objeto. Escolhe o <strong>múltiplo</strong> disponível. O pedido entra em estado <strong>Pendente</strong> e fica visível ao Gestor do Sistema.</span>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsRequestOpen(false)} disabled={reqSubmitting}>Cancelar</Button>
                            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSubmitRequest} disabled={!canCreateTagRequests || reqSubmitting} data-testid="submit-request-btn">
                                <Send size={16} className="mr-2"/>{reqSubmitting ? 'A submeter...' : 'Submeter pedido'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Painel lateral  detalhes do contacto externo + histórico de notificações */}
                <Sheet open={!!contactPanel} onOpenChange={(v) => {
                    if (!v) setContactPanel(null);
                }}>
                    <SheetContent className="w-[420px] sm:w-[480px] overflow-y-auto" data-testid="contact-panel">
                        {contactPanel && (() => {
                            const c = contactPanel.contact;
                            const tagId = contactPanel.tagId;
                            const cat = contactTypes[c.category] || contactTypes.outro || {label: c.category || 'Outro', color: '#64748b'};
                            const initials = (c.name || '').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('');
                            const history = getHistoryFor(c.id, tagId);
                            const stats = history.reduce((a, h) => {
                                a.total++;
                                if (h.status === 'delivered') a.delivered++; else a.failed++;
                                return a;
                            }, {total: 0, delivered: 0, failed: 0});
                            return (
                                <>
                                    <SheetHeader>
                                        <SheetTitle className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{backgroundColor: cat.color}}>
                                                {initials}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-secondary leading-tight">{c.name}</p>
                                                {c.nickname && <p className="text-xs text-slate-500 font-normal">"{c.nickname}"</p>}
                                            </div>
                                        </SheetTitle>
                                        <SheetDescription className="flex items-center gap-2">
                                            <Badge variant="outline" style={{borderColor: cat.color, color: cat.color}}>{cat.label}</Badge>
                                            {c.organization && <span className="text-xs text-slate-600">· {c.organization}</span>}
                                        </SheetDescription>
                                    </SheetHeader>

                                    <div className="space-y-4 mt-5">
                                        {/* Contacto */}
                                        <div className="grid grid-cols-1 gap-2 text-sm">
                                            {c.phone && (
                                                <a href={`tel:${c.phone}`} className="flex items-center gap-2 p-2 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-700">
                                                    <Send size={13} className="text-blue-600"/> {c.phone}
                                                </a>
                                            )}
                                            {c.email && (
                                                <a href={`mailto:${c.email}`} className="flex items-center gap-2 p-2 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-700">
                                                    <FileText size={13} className="text-emerald-600"/> {c.email}
                                                </a>
                                            )}
                                            {c.notes && (
                                                <div className="flex items-start gap-2 p-2 rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs">
                                                    <Info size={12} className="mt-0.5 flex-shrink-0"/>
                                                    <span>{c.notes}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Tag de origem */}
                                        <div className="p-3 rounded-md border border-slate-200 bg-white">
                                            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Notificações desta tag</p>
                                            <div className="flex items-center gap-2 mb-2">
                                                <Badge className="font-mono text-xs bg-slate-100 text-slate-800 border-slate-200">{tagId}</Badge>
                                                <span className="text-xs text-slate-500">· total notificada {(c.resourceIds || []).length} recurso{(c.resourceIds || []).length === 1 ? '' : 's'}</span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-2 text-center">
                                                <div className="p-2 rounded bg-slate-50"><p className="text-[10px] text-slate-500 uppercase">Total</p><p className="font-bold text-slate-900">{stats.total}</p></div>
                                                <div className="p-2 rounded bg-emerald-50"><p className="text-[10px] text-emerald-700 uppercase">Entregues</p><p className="font-bold text-emerald-700">{stats.delivered}</p></div>
                                                <div className="p-2 rounded bg-red-50"><p className="text-[10px] text-red-700 uppercase">Falhadas</p><p className="font-bold text-red-700">{stats.failed}</p></div>
                                            </div>
                                        </div>

                                        {/* Timeline */}
                                        <div>
                                            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Timeline de notificações</p>
                                            <div className="relative pl-4 border-l-2 border-slate-200 space-y-3" data-testid="contact-panel-timeline">
                                                {history.map(h => (
                                                    <div key={h.id} className="relative">
                                                        <div
                                                            className="absolute -left-[17px] top-1.5 w-3 h-3 rounded-full border-2 border-white"
                                                            style={{backgroundColor: h.status === 'delivered' ? '#10b981' : '#ef4444'}}
                                                        />
                                                        <div className="text-[10px] text-slate-400">{h.date}</div>
                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                            <Badge variant="outline" className="text-[9px] uppercase">{h.channel}</Badge>
                                                            <span className="text-xs font-medium text-slate-800">{EVENT_LABEL[h.event] || h.event}</span>
                                                            {h.status === 'failed' && <Badge className="bg-red-100 text-red-700 border-red-200 text-[9px]">Falhou</Badge>}
                                                        </div>
                                                        <p className="text-xs text-slate-600 mt-0.5 italic">"{h.text}"</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <Button variant="outline" className="w-full" onClick={() => navigate('/external-contacts')} data-testid="contact-panel-edit-btn">
                                            Abrir gestão de contactos
                                        </Button>
                                    </div>
                                </>
                            );
                        })()}
                    </SheetContent>
                </Sheet>
            </div>
        </Layout>
    );
};

// === Tag row reutilizada nos vários modos ===
// Gerador de histórico mock de notificações (deterministico, baseado no contactId+tagId).
const NOTIFICATION_TEMPLATES = [
    {channel: 'sms', event: 'tag_lida_entrega', text: 'A sua entrega foi confirmada via leitura da tag {tag}.'},
    {channel: 'email', event: 'tag_lida_entrega', text: 'Notificação automática: tag {tag} lida com sucesso.'},
    {channel: 'sms', event: 'ocorrencia_aberta', text: 'Foi reportada uma ocorrência na tag {tag}.'},
    {channel: 'email', event: 'tag_inativa', text: 'A tag {tag} foi marcada como inativa.'},
    {channel: 'sms', event: 'tag_lida_entrega', text: 'Confirmação de entrega: {tag}.'},
];
// hash determinístico simples  same id => same history (sem necessidade de persistência)
const stableHash = (s) => Array.from(s).reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0);
const getHistoryFor = (contactId, tagId) => {
    const seed = Math.abs(stableHash(`${contactId}:${tagId}`));
    const count = (seed % 4) + 2; // 2..5 entradas
    const out = [];
    for (let i = 0; i < count; i++) {
        const tpl = NOTIFICATION_TEMPLATES[(seed + i) % NOTIFICATION_TEMPLATES.length];
        const daysAgo = (i * 3) + ((seed + i) % 3);
        const d = new Date(Date.now() - daysAgo * 86400000 - ((seed + i) % 12) * 3600000);
        const date = d.toISOString().slice(0, 10) + ' ' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
        const status = (seed + i) % 7 === 0 ? 'failed' : 'delivered';
        out.push({id: `${contactId}-${tagId}-${i}`, channel: tpl.channel, event: tpl.event, text: tpl.text.replace('{tag}', tagId), date, status});
    }
    return out;
};

const EVENT_LABEL = {
    tag_lida_entrega: 'Entrega confirmada',
    ocorrencia_aberta: 'Ocorrência aberta',
    tag_inativa: 'Tag inativada',
};

// Pequeno indicador "Notifica X contactos" para uma tag.
const ExternalContactsCell = ({tagId, resourceId, onAvatarClick}) => {
    const {contacts, contactTypes} = useExternalContacts();
    const matched = contacts.filter(c => {
        const resourceIds = (c.resourceIds || []).map(String);
        return resourceId ? resourceIds.includes(String(resourceId)) : (c.tagIds || []).includes(tagId);
    });
    if (matched.length === 0) return <span className="text-slate-300 text-xs"></span>;
    const visible = matched.slice(0, 3);
    const remaining = matched.length - visible.length;
    const initials = (name) => (name || '').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('');
    return (
        <div className="flex items-center gap-1.5" data-testid={`tag-ec-cell-${tagId}`}>
            <div className="flex -space-x-1.5">
                {visible.map(c => {
                    const cat = contactTypes[c.category] || contactTypes.outro || {label: c.category || 'Outro', color: '#64748b'};
                    return (
                        <button
                            key={c.id}
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAvatarClick?.({contact: c, tagId});
                            }}
                            data-testid={`tag-ec-avatar-${tagId}-${c.id}`}
                            className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold text-white hover:scale-110 hover:z-10 transition-transform cursor-pointer"
                            style={{backgroundColor: cat.color}}
                            title={`${c.name} · ${cat.label}`}
                        >
                            {initials(c.name)}
                        </button>
                    );
                })}
                {remaining > 0 && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAvatarClick?.({contact: matched[0], tagId});
                        }}
                        className="w-6 h-6 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-700 hover:bg-slate-300"
                        title={matched.slice(3).map(c => c.name).join(', ')}
                    >
                        +{remaining}
                    </button>
                )}
            </div>
            <span className="text-[10px] text-slate-500">{matched.length}</span>
        </div>
    );
};

const TagRow = ({tag, onAssign, onRemoveAssignment, onAvatarClick, canAssign = false}) => (
    <TableRow className="hover:bg-slate-50">
        <TableCell className="font-mono font-bold text-slate-900">{tag.id}</TableCell>
        <TableCell><Badge variant="outline" className="text-xs">{tag.objectType}</Badge></TableCell>
        <TableCell><StatusBadgeCustom tag={tag}/></TableCell>
        <TableCell className="text-sm">{tag.resourceReference !== '-' ? <Badge variant="outline" className="font-mono">{tag.resourceReference}</Badge> : <span className="text-slate-400">-</span>}</TableCell>
        <TableCell className="text-sm">{tag.resourceName !== '-' ? <span className="font-medium text-slate-900">{tag.resourceName}</span> : <span className="text-slate-400">-</span>}</TableCell>
        <TableCell className="text-sm text-slate-600">{tag.resourceType !== '-' ? tag.resourceType : <span className="text-slate-400">-</span>}</TableCell>
        <TableCell className="text-sm text-slate-600">{tag.category !== '-' ? tag.category : <span className="text-slate-400">-</span>}</TableCell>
        <TableCell className="text-sm text-slate-600">
            <div className="flex items-center gap-2">
                {tag.user !== '-' ? <span>{tag.user}</span> : <span className="text-slate-400">-</span>}
                <ExternalContactsCell tagId={tag.id} resourceId={tag.resourceId} onAvatarClick={onAvatarClick}/>
            </div>
        </TableCell>
        <TableCell className="text-xs text-slate-600 whitespace-nowrap">
            <div className="flex items-center gap-1">
                <Calendar size={11} className="text-slate-400"/>
                <span>Criado: <span className="font-medium text-slate-700">{tag.createdAt || ''}</span></span>
            </div>
            <div className="flex items-center gap-1 text-slate-500">
                <Calendar size={11} className="text-slate-300"/>
                <span>Atualizado: <span className="font-medium">{tag.updatedAt || ''}</span></span>
            </div>
        </TableCell>
        <TableCell className="text-sm text-slate-500">{tag.lastIncident !== '-' ? tag.lastIncident : <span className="text-slate-400">-</span>}</TableCell>
        <TableCell>
            {canAssign && tag.status === 'armazem' && (
                <Button variant="outline" size="sm" className="text-blue-600 hover:text-blue-700" onClick={(e) => {
                    e.stopPropagation();
                    onAssign(tag);
                }}>
                    <Car size={14} className="mr-1"/>Atribuir
                </Button>
            )}
            {canAssign && tag.status === 'atribuida' && (
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={(e) => {
                    e.stopPropagation();
                    onRemoveAssignment(tag);
                }}>
                    <Ban size={14} className="mr-1"/>Remover
                </Button>
            )}
        </TableCell>
    </TableRow>
);

// === Group of rows: object-type header + (optional) batch sub-headers + tags ===
const GroupRows = ({group, showBatch, onAssign, onRemoveAssignment, onAvatarClick, canAssign = false, expandedOTs, expandedBatches, onToggleOT, onToggleBatch}) => {
    const isOTOpen = expandedOTs.has(group.objectTypeId);
    return (
        <>
            <TableRow className="bg-slate-100 hover:bg-slate-200 border-y-2 border-slate-200 cursor-pointer" onClick={() => onToggleOT(group.objectTypeId)}>
                <TableCell colSpan={11} className="py-2.5">
                    <div className="flex items-center gap-2 font-bold text-slate-800">
                        {isOTOpen ? <ChevronDown size={14} className="text-slate-600"/> : <ChevronRight size={14} className="text-slate-600"/>}
                        <BoxIcon size={14} className="text-slate-600"/>
                        <span>{group.objectTypeName}</span>
                        {group.objectTypeCategory && group.objectTypeCategory !== group.objectTypeName && (
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{group.objectTypeCategory}</Badge>
                        )}
                        <span className="text-slate-500 font-normal text-xs">· {group.tags.length} tag{group.tags.length === 1 ? '' : 's'}</span>
                        {showBatch && (
                            <span className="text-slate-500 font-normal text-xs">· {group.batches.length} lote{group.batches.length === 1 ? '' : 's'}</span>
                        )}
                    </div>
                </TableCell>
            </TableRow>
            {isOTOpen && (
                showBatch ? (
                    group.batches.map((batch) => {
                        const isBatchOpen = expandedBatches.has(batch.batchId);
                        return (
                            <Fragment key={'batch-' + batch.batchId}>
                                <TableRow
                                    className="bg-violet-50 hover:bg-violet-100 cursor-pointer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleBatch(batch.batchId);
                                    }}
                                >
                                    <TableCell colSpan={11} className="py-1.5">
                                        <div className="flex items-center gap-2 pl-6 text-xs">
                                            {isBatchOpen ? <ChevronDown size={12} className="text-violet-600"/> : <ChevronRight size={12} className="text-violet-600"/>}
                                            <Package size={12} className="text-violet-600"/>
                                            <span className="text-slate-600">Lote</span>
                                            <span className="font-mono font-bold text-violet-900">{batch.batchCode}</span>
                                            <span className="text-slate-500">· {batch.tags.length} tag{batch.tags.length === 1 ? '' : 's'}</span>
                                            {batch.batchData?.deliveredAt && <span className="text-slate-400 ml-2">entregue em {batch.batchData.deliveredAt}</span>}
                                        </div>
                                    </TableCell>
                                </TableRow>
                                {isBatchOpen && batch.tags.map((t) => <TagRow key={t.id} tag={t} onAssign={onAssign} onRemoveAssignment={onRemoveAssignment} onAvatarClick={onAvatarClick} canAssign={canAssign}/>)}
                            </Fragment>
                        );
                    })
                ) : (
                    group.tags.map((t) => <TagRow key={t.id} tag={t} onAssign={onAssign} onRemoveAssignment={onRemoveAssignment} onAvatarClick={onAvatarClick} canAssign={canAssign}/>)
                )
            )}
        </>
    );
};
